import React, { Suspense, useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector, useStore } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Form, Result, Skeleton, Space } from 'antd'
import { FilterOutlined } from '@ant-design/icons'
import { useMediaQuery } from 'react-responsive'
import B2BLayout from '../../layouts/b2b'
import { TasksCalendar } from '../../components/v2/tasks-calendar'
import StickyToolbar from '../../layouts/b2b/sticky-toolbar'
import FiltersList from '../../components/v2/filters'
import FiltersDrawer, { FiltersDrawerRef } from '../../components/v2/filters-drawer'
import { FiltersFastButtons } from '../../components/v2/filters-fast-buttons'
import { ModalCreateTaskByHrResponse } from '../../views/modal-create-task-searchByHrResponse'
import { getCalendarError, selectCalendarOptions } from './selectors'
import { useHeaderActions } from '../../contexts/header-actions-context'
import { HeaderAction } from '../../contexts/header-actions-context/interface'
import { useTasksEnums } from '../tasks/hooks'
import { createSession } from '../tasks/selectors'
import { CONSTANTS } from '../tasks/constants'
import { getFilters } from './filters'
import { hasPermission } from 'src/react-app/lib/permissions'
import { Permissions } from 'src/react-app/constants/permissions'

const breadcrumbs = [
  {
    href: '/tasks',
    title: 'Задачи',
  },
]

const AdminNewObjectPage: React.FC = () => {
  const [filtersForm] = Form.useForm()
  const location = useLocation()
  const navigate = useNavigate()
  const { sessionData } = useSelector(createSession)
  const store = useStore()
  const { setHeaderActions } = useHeaderActions()
  const isMobile = useMediaQuery({ maxWidth: 768 })

  const options = selectCalendarOptions(store.getState())
  const error = useSelector(getCalendarError)

  const filtersDrawerRef = useRef<FiltersDrawerRef | null>(null)
  const headerRef = useRef<HTMLDivElement | null>(null)

  const isLeader = sessionData?.organization?.id === CONSTANTS.LEADER_ORG_ID
  const isClaimLeader = useMemo(
    () => hasPermission(sessionData?.permissionsWithScope, Permissions.ACCESS_CLAIMS),
    [sessionData?.permissionsWithScope],
  )
  const isClaimOffice = sessionData?.user?.office?.id === CONSTANTS.CLAIM_OFFICE_ID
  const taskType =
    isClaimOffice || isClaimLeader
      ? 'claim'
      : sessionData?.organization?.type === CONSTANTS.ORGANIZATION_TYPE_HR
      ? 'hr'
      : 'case'

  const {
    enums,
    loading: enumsLoading,
    error: enumsError,
    montageTypes,
    measurementTypes,
    deliveryTypes,
    saleTypes,
    claimTypes,
  } = useTasksEnums({
    taskType,
    isLeader,
  })

  const pageLoading = enumsLoading

  const criticalError =
    error ||
    (enumsError
      ? {
          code: 'error' as const,
          message: enumsError,
        }
      : null)

  const currentFilters = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const result: Record<string, any> = {}

    params.forEach((value, key) => {
      if (key === 'responsible' && !isNaN(+value)) {
        result[key] = +value
      } else if (key === 'task_type') {
        result[key] = value.split(',').filter(Boolean)
      } else {
        result[key] = value
      }
    })

    return result
  }, [location.search])

  const filterFields = useMemo(() => {
    return getFilters(currentFilters, enums)
  }, [currentFilters, enums])

  const fastButtons = useMemo(() => {
    const buttons: any[] = []

    if (sessionData?.user?.id) {
      buttons.push({
        name: 'Мои задачи',
        code: 'responsible',
        value: String(sessionData.user.id),
      })
    }

    if (taskType === 'case') {
      if (saleTypes.length > 0) {
        buttons.push({
          name: 'Продажи',
          code: 'task_type',
          value: saleTypes.join(','),
        })
      }
      if (measurementTypes.length > 0) {
        buttons.push({
          name: 'Замеры',
          code: 'task_type',
          value: measurementTypes.join(','),
        })
      }
      if (deliveryTypes.length > 0) {
        buttons.push({
          name: 'Доставка',
          code: 'task_type',
          value: deliveryTypes.join(','),
        })
      }
      if (montageTypes.length > 0) {
        buttons.push({
          name: 'Монтажи',
          code: 'task_type',
          value: montageTypes.join(','),
        })
      }
    }

    if (claimTypes.length > 0) {
      buttons.push({
        name: 'Рекламации',
        code: 'task_type',
        value: claimTypes.join(','),
      })
    }

    const stageButtons = (enums?.stages || []).map((s: any) => ({
      name: s.label,
      code: 'stage',
      value: String(s.value),
    }))
    buttons.push(...stageButtons)

    return buttons
  }, [
    sessionData?.user?.id,
    taskType,
    saleTypes,
    measurementTypes,
    deliveryTypes,
    montageTypes,
    claimTypes,
    enums,
  ])

  useEffect(() => {
    const actions: HeaderAction[] = []

    if (filtersDrawerRef) {
      actions.push({
        key: 'filters',
        icon: <FilterOutlined />,
        tooltip: 'Фильтры',
        onClick: () => filtersDrawerRef.current?.toggle?.(),
      })
    }

    setHeaderActions(actions)
    return () => setHeaderActions([])
  }, [setHeaderActions])

  const changeFiltersHandler = useCallback(
    (values: Record<string, any>, replace?: boolean) => {
      const params = replace ? new URLSearchParams() : new URLSearchParams(location.search)

      Object.keys(values).forEach(key => {
        const value = values[key]
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            if (value.length > 0) {
              params.set(key, value.join(','))
            } else {
              params.delete(key)
            }
          } else {
            params.set(key, String(value))
          }
        } else {
          params.delete(key)
        }
      })

      navigate({ search: params.toString() })
    },
    [location.search, navigate],
  )

  return (
    <B2BLayout breadcrumbs={breadcrumbs}>
      {pageLoading && <Skeleton active />}
      {!pageLoading && criticalError && (
        <Result
          status={typeof criticalError === 'object' ? criticalError.code : 'error'}
          title="Ошибка"
          subTitle={typeof criticalError === 'object' ? criticalError.message : criticalError}
        />
      )}
      {!pageLoading && !criticalError && (
        <>
          <StickyToolbar
            ref={headerRef}
            collapsible={
              <Space
                style={{ width: '100%', justifyContent: 'space-between' }}
                align={'start'}
                wrap
              >
                <Space align={'start'} wrap>
                  <Suspense fallback="">
                    <ModalCreateTaskByHrResponse
                      entityType={taskType}
                      titleInputSearch={taskType === 'hr' ? 'ID отклика' : 'Номер заказа'}
                      externalEnums={enums}
                    />
                  </Suspense>
                  <FiltersList
                    filters={filterFields.filter(f => ['id'].includes(f.name))}
                    onChange={changeFiltersHandler}
                    isChangeByButtons
                    form={filtersForm}
                  />
                </Space>
                <FiltersDrawer
                  filters={filterFields}
                  onChange={changeFiltersHandler}
                  currentFilters={currentFilters}
                  ref={filtersDrawerRef}
                />
              </Space>
            }
            persistent={
              <>
                {options?.length > 0 && options.includes('tasks_quickfilters') && (
                  <FiltersFastButtons filters={fastButtons} onChange={changeFiltersHandler} />
                )}
              </>
            }
          />
          <TasksCalendar isMobile={isMobile} />
        </>
      )}
    </B2BLayout>
  )
}

export default AdminNewObjectPage
